import { useCallback, useMemo, useState } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { isScheduleRequestEventData, ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { useModalControl } from '@frontend/design-system';
import { convertScheduleRequestEventToRequest } from '../../../../../../hooks/scheduler-v3/use-convert-schedule-request-to-event';
import { useCheckPaymentsEnabled } from '../../../../../../hooks/use-check-payments-enabled';
import { ScheduleRequestModalContextType } from '../Context/ScheduleRequestModalContext';

export const useScheduleRequestModalContextValue = <
  T extends ScheduleRequest | ScheduleRequestEventData = ScheduleRequest
>({
  refetchScheduleRequestList,
  selectedRequest: selectedRequestProp = null,
  setSelectedRequest: setSelectedRequestProp,
  isScheduleRequestFormDisabled = false,
}: {
  refetchScheduleRequestList: () => void;
  selectedRequest?: T | null;
  setSelectedRequest?: (request: T | null) => void;
  isScheduleRequestFormDisabled?: boolean;
}) => {
  const [selectedScheduleRequest, setSelectedScheduleRequest] = useState<T | null>(selectedRequestProp ?? null);
  const selectedRequest = setSelectedRequestProp ? selectedRequestProp : selectedScheduleRequest;
  const setSelectedRequest = setSelectedRequestProp ?? setSelectedScheduleRequest;

  const scheduleRequestModalProps = useModalControl({
    onClose: () => {
      setTimeout(() => {
        setSelectedRequest(null);
      }, 200); // Delay to allow modal's exit animation to finish
    },
  });
  const { isPaymentsEnabled } = useCheckPaymentsEnabled(selectedRequest?.locationId);

  const handleCloseScheduleRequestModal = useCallback(() => {
    scheduleRequestModalProps.closeModal();
  }, [scheduleRequestModalProps]);

  /* when the type of T is ScheduleRequestEventData, we need to convert it to ScheduleRequest
   * to pass it correctly to the context value below and for optionally using it elsewhere as a ScheduleRequest */
  const convertedScheduleRequest = useMemo(() => {
    if (!selectedRequest) return null;
    return isScheduleRequestEventData(selectedRequest)
      ? convertScheduleRequestEventToRequest(selectedRequest)
      : selectedRequest;
  }, [selectedRequest]);

  const scheduleRequestModalContextValue: ScheduleRequestModalContextType = useMemo(
    () => ({
      scheduleRequestModalProps,
      selectedScheduleRequest: convertedScheduleRequest ?? undefined,
      handleCloseScheduleRequestModal,
      refetchScheduleRequestList,
      isPaymentsEnabled,
      isScheduleRequestFormDisabled,
    }),
    [
      convertedScheduleRequest,
      refetchScheduleRequestList,
      isPaymentsEnabled,
      isScheduleRequestFormDisabled,
      handleCloseScheduleRequestModal,
      scheduleRequestModalProps,
    ]
  );

  return {
    selectedScheduleRequest: selectedRequest,
    setSelectedScheduleRequest: setSelectedRequest,
    convertedScheduleRequest,
    scheduleRequestModalContextValue,
    isPaymentsEnabled,
    isScheduleRequestFormDisabled,
  };
};
